/**
 * Coach Engine — ATM Scope
 *
 * Per-ATM observation orchestrator. Filters trades to the named ATM strategy,
 * runs registered atm-scope analyzers, returns ranked observations.
 *
 * Different from setup/day/progress scopes:
 *   - day:      one date, all setups/ATMs
 *   - progress: all setups/ATMs, last N days
 *   - setup:    one setup, all of its tracked history
 *   - atm:      one ATM strategy, all of its tracked history
 *
 * Where setup-scope asks about ENTRY edge, atm-scope asks about EXIT
 * scaffolding — stop placement, target placement, regime fit.
 *
 * Adding a new atm-scope analyzer:
 *   1. Create the module under ../analyzers/atm/
 *   2. Push into ANALYZERS below
 *   3. Done — ranking is handled here, scope dispatch wires up in index.js.
 */

import { topObservations } from '../priorities';
import { synthesizeAndPrepend } from '../synthesis/synthesize';
import { enrichWithTemporalContext } from '../synthesis/temporalContext';
import { getRuntimeContext } from '../profile/runtimeContext';
import { buildEmptyScopeHint } from '../emptyScopeHint';
import * as atmBarContext           from '../analyzers/atm/atmBarContext';
import * as atmVersionComparison    from '../analyzers/atm/atmVersionComparison';
import * as atmVersionTrend         from '../analyzers/atm/atmVersionTrend';
import * as atmEditSuggestions      from '../analyzers/atm/atmEditSuggestions';
import * as atmBracketCalibration   from '../analyzers/composite/atmBracketCalibration';
import * as atmTuningSuggestion     from '../analyzers/composite/atmTuningSuggestion';
import * as emaProximityAtm         from '../analyzers/composite/emaProximityAtm';
import * as confluenceCompositeAtm  from '../analyzers/composite/confluenceCompositeAtm';

const ANALYZERS = [
  atmBarContext,
  atmVersionComparison,
  // Phase 17 — 3+ version arc detection (sibling of atmVersionComparison).
  // Surfaces monotonic improvement/regression and U-shape sweet-spot patterns
  // across the template's full version history.
  atmVersionTrend,
  // Composite — reads the actual bracket params (stopLossTicks, targetTicks)
  // from the latest version snapshot and compares against observed MAE/MFE
  // distributions to give prescriptive calibration advice.
  atmBracketCalibration,
  // Phase 1 of the inline-ATM-editor roadmap (v7.5.2). Reads the same
  // bracket params as atmBracketCalibration but produces STRUCTURED edit
  // suggestions ({field, currentValue, suggestedValue, confidence}) that
  // the upcoming Phase 2 inline editor can pre-fill from. Output is
  // observation-shaped for the Live Coach Feed today; same data fuels the
  // editor modal tomorrow.
  atmEditSuggestions,
  // Phase 17 — Forward-looking tuning suggestions. PATH A (per-template
  // extrapolation: "you've gone 12→16→24, try 32") emits per-template;
  // PATH B (cross-template transfer) is cross-cutting but it's nicer to
  // surface here for now since the ATM card already shows the version
  // history right next to it.
  atmTuningSuggestion,
  // Composite — ATM-scope adaptations of the setup-scope EMA proximity +
  // confluence-pair analyzers. Asks "which entry style does THIS management
  // style work with?" — different ATMs reward different entry geometries.
  emaProximityAtm,
  confluenceCompositeAtm,
];

const TIER_LEVEL = { free: 0, pro: 1 };

const analyzersForTier = (userTier = 'free') => {
  const level = TIER_LEVEL[userTier] ?? 0;
  return ANALYZERS.filter(a => (TIER_LEVEL[a.tier || 'free'] ?? 0) <= level);
};

/**
 * Run the registered atm-scope analyzers and return ranked observations.
 *
 * @param {Object} ctx
 * @param {string} ctx.atmName              The ATM strategy being analyzed (matched against trade.atmStrategy)
 * @param {Array}  ctx.allTrades            Full trades array — analyzer filters to this ATM
 * @param {string} [ctx.anchorDate]         Anchor for "recent vs prior" comparisons (today's trading day)
 * @param {string} [ctx.tier]               'free' | 'pro' — tier-gates analyzers
 * @param {number} [ctx.top]                Cap output count (default unlimited)
 * @param {Array}  [ctx.allConfluences]     Confluence library — for id/name resolution
 * @param {Array}  [ctx.allMistakes]        Mistakes library — for cross-tab mistake clustering by ATM
 * @param {Object} [ctx.atmDetails]         ATM template configurations (bracket/trail/target by name)
 * @param {Object} [ctx.setupDetails]       Setup configurations — for cross-referencing which setups pair with this ATM
 * @param {Object} [ctx.allJournalEntries]  Journal entries keyed by date — for regime/event sensitivity
 * @param {Object} [ctx.ntTemplateVersions] NinjaTrader template version history keyed by ATM name —
 *                                          enables atmVersionComparison to attribute trades to the
 *                                          specific bracket structure that was active when each was taken.
 * @param {Function} [ctx.getTradeNetPnL]
 * @param {Function} [ctx.getAccountById]
 * @returns {Array<Observation>}
 */
export const analyzeAtm = (ctx = {}) => {
  const {
    atmName, allTrades = [], anchorDate, tier = 'free', top,
    allConfluences = [], allMistakes = [],
    atmDetails = {}, setupDetails = {},
    allJournalEntries = {},
    ntTemplateVersions = {},
    futuresSpecs = {},
    getTradeNetPnL, getAccountById,
  } = ctx;
  if (!atmName) return [];
  if (!Array.isArray(allTrades) || allTrades.length === 0) return [];

  // Filter to this ATM. Trades without barAnalysis are still included —
  // individual analyzers can decide how to handle them (most skip).
  const atmTrades = allTrades.filter(t => t && t.atmStrategy === atmName);
  if (atmTrades.length === 0) return [];

  // Enrich with commission-aware netPnl. Same reason as setup scope: the
  // bar-context + version-comparison analyzers all read `t.netPnl > 0` to
  // count winners, but raw trade objects don't carry netPnl — it's
  // computed on demand from `trade.pnl` minus commissions via the
  // getTradeNetPnL callback. Without enrichment, every WR comes back as
  // 0% and no observation hits its threshold. Mirrors dayUtils.buildDayContext
  // and the setup-scope enrichment.
  const netPnlFor = (t) => {
    const acc = getAccountById ? getAccountById(t.accountId) : null;
    const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
    return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
  };
  const enrichedAtmTrades = atmTrades.map(t => ({ ...t, netPnl: netPnlFor(t) }));

  const analyzers = analyzersForTier(tier);
  const all = [];
  for (const analyzer of analyzers) {
    try {
      const result = analyzer.analyze({
        atmName,
        atmTrades: enrichedAtmTrades,
        // For composite analyzers that need cross-template visibility
        // (atmTuningSuggestion PATH B), expose the full enriched list too.
        allTrades: enrichedAtmTrades.length > 0
          ? allTrades.map(t => ({ ...t, netPnl: t.netPnl ?? netPnlFor(t) }))
          : allTrades,
        anchorDate,
        tier,
        allConfluences,
        allMistakes,
        atmDetails,
        setupDetails,
        allJournalEntries,
        ntTemplateVersions,
        futuresSpecs,  // Phase 17 — used by atmVersionComparison per-bracket attribution
        getTradeNetPnL,
        getAccountById,
      });
      if (Array.isArray(result)) all.push(...result);
      else if (result) all.push(result);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[Coach atm] ${analyzer.key || 'unknown'} failed:`, e?.message || e);
    }
  }

  if (all.length === 0) {
    const hint = buildEmptyScopeHint({ scope: 'atm', scopeId: atmName, tradeCount: enrichedAtmTrades.length });
    return hint ? [hint] : [];
  }
  // Profile-aware enrichment (see setup scope for full rationale) —
  // annotates each observation with temporal state from the Coach Profile.
  const enriched = enrichWithTemporalContext(all, getRuntimeContext());
  // Synthesis pass — fuses analyzer observations into a single top-of-feed
  // narrative ("State of <ATM>"). Same shape as setup-scope synthesis.
  const withSynthesis = synthesizeAndPrepend(enriched, { scope: 'atm', scopeId: atmName });
  const ranked = topObservations(withSynthesis, top);
  return ranked;
};
